import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import getCountries from "../../../../services/countryService";
import getTimeZones from "../../../../services/timeZoneService";
import { PanelSelectField } from "../panelFields";

/**
 * Country + Language + Timezone dropdowns. Values are the ISO country/language
 * codes and IANA timezone code (e.g. "FR" / "fr" / "Europe/Paris"); `onChange`
 * gets `{ country_code?, language_code?, timezone? }`. A value missing from the
 * fetched list shows as "(unlisted)" rather than being dropped, and changing the
 * country does not auto-reset the language.
 */

function canonicalCode(items, code) {
  if (!code) return code;
  return items.find((item) => item.code?.toLowerCase() === code.toLowerCase())?.code ?? code;
}

function findCountryByCode(countries, code) {
  if (!code) return null;
  return countries.find((c) => c.code === code) ?? null;
}

function buildCountryOptions(countries, currentCode) {
  const options = countries.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));
  if (currentCode && !options.some((o) => o.value === currentCode)) {
    options.unshift({ value: currentCode, label: `${currentCode} (unlisted)` });
  }
  return options;
}

function buildLanguageOptions(languages, currentCode) {
  const options = (languages ?? []).map((l) => ({ value: l.code, label: `${l.name} (${l.code})` }));
  if (currentCode && !options.some((o) => o.value === currentCode)) {
    options.unshift({ value: currentCode, label: `${currentCode} (unlisted)` });
  }
  return options;
}

// Value is the IANA code (what the backend stores), not the numeric row id.
function buildTimezoneOptions(timezones, currentCode) {
  const options = timezones.map((tz) => ({ value: tz.code, label: tz.code }));
  if (currentCode && !options.some((o) => o.value === currentCode)) {
    options.unshift({ value: currentCode, label: `${currentCode} (unlisted)` });
  }
  return options;
}

export default function CountryLanguageTimezoneFields({
  countryCode = "",
  languageCode = "",
  timezoneCode = "",
  onChange,
}) {
  // `null` = not loaded yet; `[]` = loaded empty.
  const [countries, setCountries] = useState(null);
  const [timezones, setTimezones] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([getCountries({ enabledOnly: false }), getTimeZones()]).then(([countryResult, tzResult]) => {
      if (cancelled) return;
      setCountries(countryResult.status === "fulfilled" ? countryResult.value?.data ?? [] : []);
      const rawTz = tzResult.status === "fulfilled" ? tzResult.value?.data ?? [] : [];
      setTimezones([...rawTz].sort((a, b) => a.code.localeCompare(b.code)));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const countriesLoaded = countries !== null;
  const timezonesLoaded = timezones !== null;

  const resolvedCountryCode = countriesLoaded ? canonicalCode(countries, countryCode) : countryCode;

  const selectedCountry = useMemo(
    () => (countriesLoaded ? findCountryByCode(countries, resolvedCountryCode) : null),
    [countriesLoaded, countries, resolvedCountryCode]
  );

  const resolvedLanguageCode = canonicalCode(selectedCountry?.languages ?? [], languageCode);
  const resolvedTimezoneCode = timezonesLoaded ? canonicalCode(timezones, timezoneCode) : timezoneCode;

  const countryOptions = useMemo(
    () => (countriesLoaded ? buildCountryOptions(countries, resolvedCountryCode) : []),
    [countriesLoaded, countries, resolvedCountryCode]
  );
  const languageOptions = useMemo(
    () => (countriesLoaded ? buildLanguageOptions(selectedCountry?.languages, resolvedLanguageCode) : []),
    [countriesLoaded, selectedCountry, resolvedLanguageCode]
  );
  const timezoneOptions = useMemo(
    () => (timezonesLoaded ? buildTimezoneOptions(timezones, resolvedTimezoneCode) : []),
    [timezonesLoaded, timezones, resolvedTimezoneCode]
  );

  // Don't auto-reset language: if it's invalid for the new country, it
  // appears as "(unlisted)" so the user can change it explicitly.
  const handleCountryChange = (nextCode) => onChange?.({ country_code: nextCode });

  const handleLanguageChange = (nextCode) => onChange?.({ language_code: nextCode });
  const handleTimezoneChange = (nextCode) => onChange?.({ timezone: nextCode });

  return (
    <Row>
      <Col md={4}>
        <PanelSelectField
          label="Country"
          value={resolvedCountryCode ?? ""}
          onChange={handleCountryChange}
          options={countryOptions}
          disabled={!countriesLoaded}
          placeholder={countriesLoaded ? "— Select —" : "Loading…"}
        />
      </Col>
      <Col md={4}>
        <PanelSelectField
          label="Language"
          value={resolvedLanguageCode ?? ""}
          onChange={handleLanguageChange}
          options={languageOptions}
          disabled={!countriesLoaded}
          placeholder={countriesLoaded ? "— Select —" : "Loading…"}
        />
      </Col>
      <Col md={4}>
        <PanelSelectField
          label="Timezone"
          value={resolvedTimezoneCode ?? ""}
          onChange={handleTimezoneChange}
          options={timezoneOptions}
          disabled={!timezonesLoaded}
          placeholder={timezonesLoaded ? "— Select —" : "Loading…"}
        />
      </Col>
    </Row>
  );
}
